'use client'

import {useState, useEffect} from 'react'
import {
    Dialog,
    DialogPanel,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
} from '@headlessui/react'
import {
    Bars3Icon,
    ChartPieIcon,
    CursorArrowRaysIcon,
    FingerPrintIcon,
    SquaresPlusIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import {LayoutGrid} from 'lucide-react';
import {ChevronDownIcon, PhoneIcon, PlayCircleIcon, RectangleGroupIcon} from '@heroicons/react/20/solid'
import BtnAccount from "@/components/Layout/Header/BtnAccount";
import BtnCart from "@/components/Layout/Header/BtnCart";
import BtnFavorites from "@/components/Layout/Header/BtnFavorites";
import SearchLineModal from "@/components/Search/SearchLineModal";
import SearchLineModalMobile from "@/components/Search/SearchLineModalMobile";
import Lang from "@/components/Layout/Header/LangOption";
import {useLanguageConfig} from "@/lib/config/useCurrentLanguage";
import {useT} from '@/lib/config/useTranslations'
import {siteConfig} from '@/lib/config/site-config'
import {useMenu} from '@/hooks/useMenu'
import useFavorites from "@/hooks/useFavorites";
import {useLanguage} from '@/lib/config/useLanguage';

const products = [
    {
        name: 'Кольца',
        description: 'Get a better understanding where your traffic is coming from',
        href: '/shop/category/koltsa',
        icon: ChartPieIcon,
    },
    {
        name: 'Серьги',
        description: 'Speak directly to your customers with our engagement tool',
        href: '/shop/category/sergy',
        icon: CursorArrowRaysIcon,
    },
    {
        name: 'Подвески',
        description: 'Your customers’ data will be safe and secure',
        href: '/shop/category/podveski',
        icon: SquaresPlusIcon,
    },
]
const callsToAction = [
   /* {name: 'Watch demo', href: '#', icon: PlayCircleIcon},
    {name: 'Contact sales', href: '#', icon: PhoneIcon},
    {name: 'View all products', href: '#', icon: RectangleGroupIcon},*/
]
const navigation = {
    categories: [
        {
            id: 'women',
            name: 'Women',
            featured: [
                {
                    name: 'New Arrivals',
                    href: '#',
                    imageSrc: 'https://tailwindcss.com/plus-assets/img/ecommerce-images/mega-menu-category-01.jpg',
                    imageAlt: 'Models sitting back to back, wearing Basic Tee in black and bone.',
                },
                {
                    name: 'Basic Tees',
                    href: '#',
                    imageSrc: 'https://tailwindcss.com/plus-assets/img/ecommerce-images/mega-menu-category-02.jpg',
                    imageAlt: 'Close up of Basic Tee fall bundle with off-white, ochre, olive, and black tees.',
                },
                {
                    name: 'Accessories',
                    href: '#',
                    imageSrc: 'https://tailwindcss.com/plus-assets/img/ecommerce-images/mega-menu-category-03.jpg',
                    imageAlt: 'Model wearing minimalist watch with black wristband and white watch face.',
                },
            ],
            sections: [
                [
                    {
                        id: 'shoes',
                        name: 'Кольца',
                        href: '/shop/category/koltsa',
                        items: [
                            {name: 'Бриллиант', href: '/shop/category/brilliant'},
                        ],
                    },
                    {
                        id: 'collection',
                        name: 'Серьги',
                        href: '/shop/category/sergy',
                        items: [
                            {name: 'Декоративные', href: '/shop/category/dekorative'},
                        ],
                    },
                ],
                [
                    {
                        id: 'clothing',
                        name: 'Подвески',
                        href: '/shop/category/podveski',
                        items: [
                            {name: 'Шармы', href: '/shop/category/begunok'},
                        ],
                    },
                    {
                        id: 'accessories',
                        name: 'Цепи',
                        href: '/shop/category/tsepi',
                        items: [
                            {name: 'Бисмарк', href: '/shop/category/tsepi'},
                        ],
                    },
                ],
                [
                    {
                        id: 'brands',
                        name: 'Колье',
                        href: '/shop/category/kole',
                        items: [
                            {name: 'Классика', href: '/shop/category/klassika'},
                        ],
                    },
                ],
            ],
        },
    ],
}

export default function Example() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const {getHomePath} = useLanguageConfig()
    const {t} = useT()
    const {menuData, loading, error} = useMenu()
    const { count: favoritesCount } = useFavorites()
    const { localizePath } = useLanguage()
    
    // Формируем структуру меню из API данных
    const getMenuStructure = () => {
        if (!menuData || !menuData.data || !menuData.data.categories) {
            return navigation.categories // fallback к статическим данным
        }
        
        const apiCategories = menuData.data.categories
        const sections = []
        
        // Используем все категории первого уровня (с детьми и без)
        const categoriesAll = apiCategories
        
        // Группируем категории по колонкам (максимум 5 колонок)
        const maxColumns = 5
        const itemsPerColumn = Math.ceil(categoriesAll.length / maxColumns)
        
        for (let i = 0; i < maxColumns; i++) {
            const columnItems = []
            const startIndex = i * itemsPerColumn
            const endIndex = Math.min(startIndex + itemsPerColumn, categoriesAll.length)
            
            for (let j = startIndex; j < endIndex; j++) {
                const category = categoriesAll[j]
                columnItems.push({
                    id: category.id,
                    name: category.name,
                    href: localizePath(category.href || '/shop'), // Локализуем ссылку на категорию
                    items: (category.children && category.children.length > 0)
                        ? category.children.map(child => ({ 
                            name: child.name, 
                            href: localizePath(child.href || '/shop') 
                        }))
                        : []
                })
            }
            
            sections.push(columnItems)
        }
        
        const result = [{
            id: 'catalog',
            name: 'Каталог',
            sections: sections
        }]
        return result
    }
    
    const dynamicNavigation = {
        categories: getMenuStructure()
    }
    
    return (
        <header className="relative isolate z-10 bg-white">
            <nav aria-label="Global"
                 className="w-full max-w-[1536px] mx-auto flex items-center justify-between p-3 px-4 lg:px-8">
                <div className="flex me-12">
                    <a href={getHomePath()} className="-m-1.5 p-1.5">
                        <span className={'text-xl'}></span>
                         <img
                            alt="shop"
                            src={siteConfig.general.logo}
                            className="h-8"
                        />
                    </a>
                </div>
                <div className="flex lg:hidden gap-3">
                    <SearchLineModalMobile/>
                    <button
                        type="button"
                        onClick={() => setMobileMenuOpen(true)}
                        className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                    >
                        <span className="sr-only">Open main menu</span>
                        <Bars3Icon aria-hidden="true" className="size-6"/>
                    </button>
                </div>
                <PopoverGroup className="hidden lg:flex items-center lg:flex-1 lg:gap-x-5">
                    <Popover>
                        <PopoverButton
                            className="rounded-xl flex flex-row items-center gap-2 bg-gray-100 px-5 py-3 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
                            <LayoutGrid aria-hidden="true" className="size-5 flex-none"/>
                            {t('navigation.catalog')}
                        </PopoverButton>

                        <PopoverPanel
                            transition
                            className="absolute inset-x-0 top-0 -z-10 bg-white pt-14 shadow-lg ring-1 ring-gray-900/5 transition data-closed:-translate-y-1 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in"
                        >

                            <div
                                className="mx-auto grid max-w-[1536px] text-sm px-4 grid-cols-5 gap-x-4 py-10 lg:px-8 xl:gap-x-8">
                                {loading ? (
                                    <div className="col-span-5 text-center py-10">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                                        <p className="mt-2 text-gray-500">Загрузка меню...</p>
                                    </div>
                                ) : error ? (
                                    <div className="col-span-5 text-center py-10">
                                        <p className="text-red-500">Ошибка загрузки меню: {error}</p>
                                        <p className="text-gray-500 mt-2">Используются статические данные</p>
                                    </div>
                                ) : (
                                    dynamicNavigation.categories[0]?.sections?.map((column, columnIdx) => (
                                        <div key={columnIdx} className="space-y-10">
                                            {column.map((section) => (
                                                <div key={section.name}>
                                                    <a href={section.href}
                                                       id={`${dynamicNavigation.categories[0].id}-${section.id}-heading-mobile`}
                                                       className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                                                        {section.name}
                                                    </a>
                                                    <ul
                                                        role="list"
                                                        aria-labelledby={`${dynamicNavigation.categories[0].id}-${section.id}-heading-mobile`}
                                                        className="mt-6 flex flex-col space-y-6"
                                                    >
                                                        {section.items?.map((item) => (
                                                            <li key={item.name} className="flow-root">
                                                                <a href={item.href}
                                                                   className="-m-2 block p-2 text-gray-500">
                                                                    {item.name}
                                                                </a>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    ))
                                )}
                            </div>
                        </PopoverPanel>
                    </Popover>

                    <SearchLineModal/>

                </PopoverGroup>
                <div className="hidden lg:flex items-center lg:ms-32 lg:justify-end gap-3">
                    <Lang/>
                    <BtnFavorites/>
                    <BtnAccount/>
                    <BtnCart/>
                </div>
            </nav>
            <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
                <div className="fixed inset-0 z-50"/>
                <DialogPanel
                    className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10  px-4 lg:px-8">
                    <div className="flex items-center justify-between">
                        <a href={getHomePath()} className="-m-1.5 p-1.5">
                            <span className={'text-xl'}></span>
                            <img
                                alt="shop"
                                src={siteConfig.general.logo}
                                className="h-8"
                            />
                        </a>
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(false)}
                            className="-m-2.5 rounded-md p-2.5 text-gray-700"
                        >
                            <span className="sr-only">Close menu</span>
                            <XMarkIcon aria-hidden="true" className="size-6"/>
                        </button>
                    </div>
                    <div className="mt-6 pb-6 border-b border-gray-200">
                        <div className="flex flex-row items-center justify-between">
                            <Lang />
                            <a
                                href={localizePath('/favorites')}
                                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                            >
                                Избранное
                                {favoritesCount > 0 && (
                                    <span className="inline-flex min-w-[1.75rem] items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">
                                        {favoritesCount}
                                    </span>
                                )}
                            </a>
                        </div>
                    </div>
                    <div className="mt-6 flow-root">
                        <div className="-my-6 divide-y divide-gray-500/10">
                            <div className="space-y-2 py-6">
                                <Disclosure as="div" className="-mx-3">
                                    <DisclosureButton
                                        className="group flex w-full items-center justify-between rounded-lg py-2 pr-3.5 pl-3 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                                        Каталог
                                        <ChevronDownIcon aria-hidden="true"
                                                         className="size-5 flex-none group-data-open:rotate-180"/>
                                    </DisclosureButton>
                                    <DisclosurePanel className="mt-2 space-y-2">
                                        {loading ? (
                                            <div className="pl-6 py-2 text-sm text-gray-500">
                                                Загрузка...
                                            </div>
                                        ) : error ? (
                                            <div className="pl-6 py-2 text-sm text-red-500">
                                                Ошибка загрузки меню
                                            </div>
                                        ) : (
                                            dynamicNavigation.categories[0]?.sections?.flat().map((section) => (
                                                <div key={section.id}>
                                                    <a href={section.href}
                                                       className="pl-6 py-1 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors block">
                                                        {section.name}
                                                    </a>
                                                    {section.items?.map((item) => (
                                                        <DisclosureButton
                                                            key={item.name}
                                                            as="a"
                                                            href={item.href}
                                                            className="block rounded-lg py-2 pr-3 pl-8 text-sm/7 font-semibold text-gray-900 hover:bg-gray-50"
                                                        >
                                                            {item.name}
                                                        </DisclosureButton>
                                                    ))}
                                                </div>
                                            ))
                                        )}
                                    </DisclosurePanel>
                                </Disclosure>
                                <a
                                    href={localizePath('/shop')}
                                    className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                                >
                                    Акции
                                </a>
                                <a
                                    href={localizePath('/shop')}
                                    className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                                >
                                    О компании
                                </a>
                            </div>
                        </div>
                    </div>
                </DialogPanel>
            </Dialog>
        </header>
    )
}
